import { openSignedUrl } from '@/lib/openSignedUrl';
import { ContractInsightsData } from '@/pages/dashboard/ContractInsights';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { StatCard, CurrencyDisplay } from '@/components/ui/stat-card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { format } from 'date-fns';
import { coverExpiryStatus, daysToExpiry } from '@/lib/coverExpiry';
import { Award, Plus, Pencil, ExternalLink, AlertTriangle, CheckCircle2, XCircle } from 'lucide-react';

interface Props {
    data: ContractInsightsData;
    onAddRoadworthiness?: () => void;
    onEditRoadworthiness?: (id: number) => void;
}

export default function ContractInsightRoadworthiness({ data, onAddRoadworthiness, onEditRoadworthiness }: Props) {
    // All roadworthiness records sorted by issue date (newest first)
    const roadworthinessRecords = (data.vehicle_roadworthiness || []).sort((a, b) =>
        new Date(b.expires_at).getTime() - new Date(a.expires_at).getTime()
    );

    const latestRecord = roadworthinessRecords[0];
    const expiryDate = latestRecord?.expires_at
        ? new Date(latestRecord.expires_at)
        : null;
    // Good through its expiry date, on the Ghana day (G6): the one rule, in `coverExpiryStatus`.
    // The day count is Ghana days to the last day of cover, so 0 on that day.
    const daysUntilExpiry = latestRecord ? daysToExpiry(latestRecord.expires_at) : null;
    const latestStatus = latestRecord ? coverExpiryStatus(latestRecord.expires_at) : null;
    const isExpired = latestStatus === 'expired';
    const isExpiringSoon = latestStatus === 'expiring';
    const isValid = latestStatus === 'expiring' || latestStatus === 'valid';


    const totalRecords = roadworthinessRecords.length;

    return (
        <div className="space-y-6">
            {/* Alerts */}
            {(isExpiringSoon || isExpired) && (
                <div className="space-y-3">
                    {isExpired && (
                        <Alert variant="destructive">
                            <AlertTriangle className="h-4 w-4" />
                            <AlertDescription>
                                Roadworthiness certificate has expired. Was valid until {format(expiryDate!, 'MMM d, yyyy')}.
                            </AlertDescription>
                        </Alert>
                    )}
                    {isExpiringSoon && !isExpired && (
                        <Alert className="border-amber-500/50 bg-amber-500/10">
                            <AlertTriangle className="h-4 w-4 text-amber-600" />
                            <AlertDescription className="text-amber-700">
                                Roadworthiness expires in {daysUntilExpiry} days ({format(expiryDate!, 'MMM d, yyyy')}).
                            </AlertDescription>
                        </Alert>
                    )}
                </div>
            )}

            {/* Stats Cards */}
            <div className="grid gap-4 md:grid-cols-4">
                <StatCard
                    title="Status"
                    value={
                        <div className="flex items-center gap-2">
                            {isValid ? (
                                <>
                                    <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                                    <span className="text-emerald-600">Valid</span>
                                </>
                            ) : isExpired ? (
                                <>
                                    <XCircle className="h-5 w-5 text-red-600" />
                                    <span className="text-red-600">Expired</span>
                                </>
                            ) : (
                                <span className="text-muted-foreground">N/A</span>
                            )}
                        </div>
                    }
                    description="Current status of the vehicle's roadworthiness certificate."
                />
                <StatCard
                    title="Expires On"
                    value={expiryDate ? format(expiryDate, 'MMM d, yyyy') : 'N/A'}
                    description="Expiry date of the current roadworthiness certificate. Valid for 1 year from issue."
                />
                <StatCard
                    title="Total Certificates"
                    value={`${totalRecords} Records`}
                    description="Total number of roadworthiness certificates on file for this vehicle."
                />
                <StatCard
                    title="Days Remaining"
                    value={isValid && daysUntilExpiry !== null ? `${daysUntilExpiry} days` : 'N/A'}
                    description="Number of days until the current certificate expires."
                />
            </div>

            {/* Roadworthiness History */}
            <Card className="border border-border/40 bg-gradient-to-br from-background via-background to-emerald-500/5">
                <CardHeader className="flex flex-row items-center justify-between pb-4">
                    <CardTitle>
                        Certificate History
                    </CardTitle>
                    <div className="flex items-center gap-2">
                        <Badge variant="secondary" className="font-mono">
                            {totalRecords} Records
                        </Badge>
                        {onAddRoadworthiness && (
                            <Button variant="outline" size="sm" onClick={onAddRoadworthiness} className="h-8">
                                <Plus className="h-4 w-4 mr-1" />
                                Add
                            </Button>
                        )}
                    </div>
                </CardHeader>
                <CardContent>
                    {roadworthinessRecords.length > 0 ? (
                        <div className="space-y-4">
                            {roadworthinessRecords.map((record, index) => {
                                const recordExpiryDate = new Date(record.expires_at);
                                const recordDaysUntilExpiry = daysToExpiry(record.expires_at);
                                const recordStatus = coverExpiryStatus(record.expires_at);
                                const recordIsExpired = recordStatus === 'expired';
                                const recordIsExpiringSoon = recordStatus === 'expiring';
                                const isCurrent = index === 0;

                                return (
                                    <div
                                        key={record.id}
                                        className={`group flex items-start justify-between gap-4 pb-4 border-b border-border/40 last:border-0 last:pb-0 ${isCurrent ? 'relative' : ''}`}
                                    >
                                        <div className="flex items-start gap-3">
                                            <div className={`mt-1 h-10 w-10 rounded-lg flex items-center justify-center border ${isCurrent
                                                ? recordIsExpired
                                                    ? 'bg-red-50 dark:bg-red-900/10 border-red-100 dark:border-red-900/20 text-red-600 dark:text-red-400'
                                                    : recordIsExpiringSoon
                                                        ? 'bg-amber-50 dark:bg-amber-900/10 border-amber-100 dark:border-amber-900/20 text-amber-600 dark:text-amber-400'
                                                        : 'bg-muted border-border text-foreground'
                                                : 'bg-muted/50 border-border/50 text-muted-foreground'}`}
                                            >
                                                <Award className="h-5 w-5" />
                                            </div>
                                            <div>
                                                <div className="flex items-center gap-2 mb-0.5">
                                                    <span className="font-medium text-sm">
                                                        Expires: {format(new Date(record.expires_at), 'MMM d, yyyy')}
                                                    </span>
                                                    {isCurrent && !recordIsExpired && (
                                                        <Badge className="text-[10px] h-5 px-1.5 bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800">
                                                            Current
                                                        </Badge>
                                                    )}
                                                    {recordIsExpired && (
                                                        <Badge variant="destructive" className="text-[10px] h-5 px-1.5">
                                                            Expired
                                                        </Badge>
                                                    )}
                                                </div>
                                                <p className="text-sm text-muted-foreground">
                                                    Expires {format(recordExpiryDate, 'MMM d, yyyy')}
                                                    {isCurrent && !recordIsExpired && !recordIsExpiringSoon && (
                                                        <span className="text-emerald-600 dark:text-emerald-400 ml-1">
                                                            ({recordDaysUntilExpiry} days remaining)
                                                        </span>
                                                    )}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-1">
                                            {record.certificate_url && (
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="h-8 w-8 p-0"
                                                    onClick={() => openSignedUrl(record.certificate_url!)}
                                                >
                                                    <ExternalLink className="h-3.5 w-3.5" />
                                                </Button>
                                            )}
                                            {onEditRoadworthiness && (
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                                                    onClick={() => onEditRoadworthiness(record.id)}
                                                >
                                                    <Pencil className="h-3.5 w-3.5" />
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="text-center py-12 text-muted-foreground border-2 border-dashed rounded-xl bg-muted/20">
                            <Award className="h-8 w-8 mx-auto mb-3 opacity-30" />
                            <p className="text-sm font-medium">No certificates on file</p>
                            <p className="text-xs text-muted-foreground mt-1">Add a roadworthiness certificate to track compliance</p>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
